<template>
  <div class="metrics-tab">
    <div v-if="provisional" class="chip-row">
      <span class="chip provisional">Provisional thresholds</span>
    </div>

    <section
      v-for="group in groups"
      :key="group.title"
      class="metric-group"
      :data-test="`group-${group.title.toLowerCase()}`"
    >
      <header class="metric-group-head">
        <h2 class="metric-group-title">{{ group.title }}</h2>
        <div class="metric-group-rule" />
      </header>
      <div class="metric-grid">
        <MetricCard
          v-for="def in group.metrics"
          :key="def.id"
          :def="def"
          :envelope="env[def.id]"
          :sprints-for-spark="sparkSprints"
          @open="(d, e) => emit('open', d, e)"
        />
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { MetricEnvelope, SprintDetail, SprintListItem } from "@/api/types";
import { METRIC_GROUPS, type MetricDef } from "@/lib/metricDefs";
import MetricCard from "@/components/MetricCard.vue";

const props = defineProps<{
  sprint: SprintDetail;
  /** Optional sibling sprints (newest-first) to seed sparklines. */
  sprintsList?: SprintListItem[];
}>();

const emit = defineEmits<{
  (e: "open", def: MetricDef, env: MetricEnvelope | null | undefined): void;
}>();

const groups = METRIC_GROUPS;

const env = computed<Record<string, MetricEnvelope>>(() => {
  return (props.sprint.metrics ?? {}) as Record<string, MetricEnvelope>;
});

const provisional = computed(() => {
  const meta = (env.value._meta ?? {}) as Record<string, unknown>;
  return props.sprint.provisional_thresholds === true || meta.provisional_thresholds === true;
});

/**
 * Sparklines render chronologically (oldest → newest). The store hands us the
 * sprints list newest-first, so reverse a window of 6.
 */
const sparkSprints = computed<SprintListItem[]>(() => {
  const list = props.sprintsList ?? [];
  return list.slice(0, 6).slice().reverse();
});
</script>

<style scoped>
.metrics-tab { display: flex; flex-direction: column; gap: 24px; }
.chip-row { display: flex; gap: 8px; }
.chip.provisional {
  display: inline-flex; padding: 2px 8px; border-radius: 999px; font-size: 12px;
  background: #fff7ed; color: #9a3412; border: 1px solid #fdba74;
}
.metric-group-head { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; }
.metric-group-title {
  font-size: 13px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--text-muted, #6b7280); margin: 0;
}
.metric-group-rule { flex: 1; height: 1px; background: var(--surface-border, #e5e7eb); }
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
}
</style>
